import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    message,
    Card,
    Popconfirm,
    Tooltip,
    Typography,
    Drawer,
    Divider,
    Empty,
    Dropdown
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    SafetyCertificateOutlined,
    SyncOutlined,
    EyeOutlined,
    ThunderboltOutlined
} from '@ant-design/icons';
import evmService from '../../../shared/services/evmService';
import { formatDateTime } from '../../../shared/utils/date';
import SgRuleFields, { RULE_DEFAULTS, rulePayload, SG_TEMPLATES, FamilyTag, protocolLabel, remoteLabel, portsLabel } from '../../../shared/components/evm/SgRuleFields';

const { TextArea } = Input;
const { Text } = Typography;

const MySecurityGroup = () => {
    const { t } = useTranslation();
    const [securityGroups, setSecurityGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [rulesDrawerVisible, setRulesDrawerVisible] = useState(false);
    const [addRuleModalVisible, setAddRuleModalVisible] = useState(false);
    const [currentSg, setCurrentSg] = useState(null);
    const [rules, setRules] = useState([]);
    const [rulesLoading, setRulesLoading] = useState(false);

    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();
    const [ruleForm] = Form.useForm();

    useEffect(() => {
        loadSecurityGroups();
    }, []);

    const loadSecurityGroups = async () => {
        setLoading(true);
        try {
            const response = await evmService.getSecurityGroupList();
            if (response.status === 'success') {
                setSecurityGroups(response.data?.security_groups || []);
            } else {
                message.error(response.msg || t('Failed to load security groups'));
            }
        } catch (error) {
            message.error(t('Failed to load security groups'));
        } finally {
            setLoading(false);
        }
    };

    const showCreateModal = () => {
        createForm.resetFields();
        setCreateModalVisible(true);
    };

    const showEditModal = (sg) => {
        setCurrentSg(sg);
        editForm.setFieldsValue(sg);
        setEditModalVisible(true);
    };

    const showRulesDrawer = async (sg) => {
        setCurrentSg(sg);
        setRules([]);
        setRulesDrawerVisible(true);
        setRulesLoading(true);
        try {
            const response = await evmService.getSecurityGroup(sg.id);
            if (response.status === 'success') {
                setRules(response.data?.rules || []);
            } else {
                message.error(response.msg || t('Failed to load rules'));
            }
        } catch (error) {
            message.error(t('Failed to load rules'));
        } finally {
            setRulesLoading(false);
        }
    };

    const showAddRuleModal = () => {
        ruleForm.resetFields();
        ruleForm.setFieldsValue(RULE_DEFAULTS);
        setAddRuleModalVisible(true);
    };

    const handleCreate = async (values) => {
        try {
            const response = await evmService.createSecurityGroup(values);
            if (response.status === 'success') {
                message.success(t('Security group created'));
                setCreateModalVisible(false);
                loadSecurityGroups();
            } else {
                message.error(response.msg || t('Failed to create security group'));
            }
        } catch (error) {
            message.error(t('Failed to create security group'));
        }
    };

    const handleUpdate = async (values) => {
        try {
            const response = await evmService.updateSecurityGroup(currentSg.id, values);
            if (response.status === 'success') {
                message.success(t('Security group updated'));
                setEditModalVisible(false);
                loadSecurityGroups();
            } else {
                message.error(response.msg || t('Update failed'));
            }
        } catch (error) {
            message.error(t('Update failed'));
        }
    };

    const handleDelete = async (sg) => {
        try {
            const response = await evmService.deleteSecurityGroup(sg.id);
            if (response.status === 'success') {
                message.success(t('Security group deleted'));
                loadSecurityGroups();
            } else {
                message.error(response.msg || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed'));
        }
    };

    const reloadRules = async () => {
        const sgResponse = await evmService.getSecurityGroup(currentSg.id);
        if (sgResponse.status === 'success') {
            setRules(sgResponse.data?.rules || []);
        }
    };

    const handleAddRule = async (values) => {
        try {
            const response = await evmService.addSecurityGroupRule(currentSg.id, rulePayload(values));
            if (response.status === 'success') {
                message.success(t('Rule added'));
                setAddRuleModalVisible(false);
                reloadRules();
            } else {
                message.error(response.msg || t('Failed to add rule'));
            }
        } catch (error) {
            message.error(t('Failed to add rule'));
        }
    };

    const addTemplate = async (key) => {
        try {
            for (const rule of SG_TEMPLATES[key]) {
                const response = await evmService.addSecurityGroupRule(currentSg.id, rule);
                if (response.status !== 'success') {
                    message.error(response.msg || t('Failed to add rule'));
                    break;
                }
            }
        } catch (error) {
            message.error(t('Failed to add rule'));
        }
        reloadRules();
    };

    const handleDeleteRule = async (ruleId) => {
        try {
            const response = await evmService.deleteSecurityGroupRule(currentSg.id, ruleId);
            if (response.status === 'success') {
                message.success(t('Rule deleted'));
                setRules(rules.filter(r => r.id !== ruleId));
            } else {
                message.error(response.msg || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed'));
        }
    };

    const getDirectionTag = (direction) => {
        return direction === 'ingress' ? (
            <Tag color="blue">{t('Inbound')}</Tag>
        ) : (
            <Tag color="orange">{t('Outbound')}</Tag>
        );
    };

    const getActionTag = (action) => {
        return action === 'accept' ? (
            <Tag color="green">{t('Allow')}</Tag>
        ) : (
            <Tag color="red">{t('Deny')}</Tag>
        );
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60
        },
        {
            title: t('Name'),
            dataIndex: 'name',
            key: 'name',
            width: 180
        },
        {
            title: t('Description'),
            dataIndex: 'description',
            key: 'description',
            width: 250,
            ellipsis: true,
            render: (text) => text || '-'
        },
        {
            title: t('Rule count'),
            dataIndex: 'rule_count',
            key: 'rule_count',
            width: 80,
            render: (count) => count || 0
        },
        {
            title: t('Linked instances'),
            dataIndex: 'vm_count',
            key: 'vm_count',
            width: 80,
            render: (count) => count || 0
        },
        {
            title: t('Created at'),
            dataIndex: 'created_at',
            key: 'created_at',
            width: 160,
            render: (v) => formatDateTime(v)
        },
        {
            title: t('Actions'),
            key: 'action',
            fixed: 'right',
            width: 160,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title={t('View rules')}>
                        <Button
                            size="small"
                            type="primary"
                            icon={<EyeOutlined />}
                            onClick={() => showRulesDrawer(record)}
                        />
                    </Tooltip>
                    <Tooltip title={t('Edit')}>
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => showEditModal(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title={t('Delete this security group?')}
                        okText={t('Delete')}
                        cancelText={t('Cancel')}
                        onConfirm={() => handleDelete(record)}
                        disabled={record.vm_count > 0}
                    >
                        <Tooltip title={record.vm_count > 0 ? t('Cannot delete: linked to an instance') : t('Delete')}>
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                disabled={record.vm_count > 0}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const ruleColumns = [
        {
            title: t('Direction'),
            dataIndex: 'direction',
            key: 'direction',
            width: 100,
            render: (direction) => getDirectionTag(direction)
        },
        {
            title: t('Protocol'),
            dataIndex: 'protocol',
            key: 'protocol',
            width: 80,
            render: (protocol) => <Tag>{protocolLabel(protocol, t)}</Tag>
        },
        {
            title: t('Address families'),
            key: 'family',
            width: 80,
            render: (_, record) => <FamilyTag rule={record} />
        },
        {
            title: t('Port range'),
            key: 'ports',
            width: 120,
            render: (_, record) => portsLabel(record, t)
        },
        {
            title: t('Source/destination'),
            key: 'remote_ip_prefix',
            width: 140,
            render: (_, record) => remoteLabel(record, t)
        },
        {
            title: t('Policy'),
            dataIndex: 'action',
            key: 'action',
            width: 80,
            render: (action) => getActionTag(action)
        },
        {
            title: '',
            key: 'delete',
            width: 50,
            render: (_, record) => (
                <Popconfirm
                    title={t('Delete this rule?')}
                    okText={t('Delete')}
                    cancelText={t('Cancel')}
                    onConfirm={() => handleDeleteRule(record.id)}
                >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            )
        }
    ];

    return (
        <div>
            <Card
                title={<span><SafetyCertificateOutlined /> {t('Security groups')}</span>}
                extra={
                    <Space>
                        <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>
                            {t('Create security group')}
                        </Button>
                        <Button icon={<SyncOutlined />} onClick={loadSecurityGroups}>
                            {t('Refresh')}
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={securityGroups}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 10 }}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={t('No security groups yet. Click "Create security group" in the top right.')}
                            />
                        )
                    }}
                />
            </Card>

            {/* Create Security Group Modal */}
            <Modal
                title={t('Create security group')}
                open={createModalVisible}
                onCancel={() => setCreateModalVisible(false)}
                onOk={() => createForm.submit()}
            >
                <Form form={createForm} layout="vertical" onFinish={handleCreate}>
                    <Form.Item
                        name="name"
                        label={t('Name')}
                        rules={[{ required: true, message: t('Please enter a name') }]}
                    >
                        <Input maxLength={100} placeholder={t('E.g. web-sg')} />
                    </Form.Item>
                    <Form.Item name="description" label={t('Description')}>
                        <TextArea rows={3} maxLength={500} placeholder={t('Optional')} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Edit Security Group Modal */}
            <Modal
                title={t('Edit security group')}
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onOk={() => editForm.submit()}
            >
                <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
                    <Form.Item
                        name="name"
                        label={t('Name')}
                        rules={[{ required: true, message: t('Please enter a name') }]}
                    >
                        <Input maxLength={100} placeholder={t('Security group name')} />
                    </Form.Item>
                    <Form.Item name="description" label={t('Description')}>
                        <TextArea rows={3} maxLength={500} placeholder={t('Optional')} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Rules Drawer */}
            <Drawer
                title={t('Security group rules: {{value}}', { value: currentSg?.name || '' })}
                width={700}
                open={rulesDrawerVisible}
                onClose={() => setRulesDrawerVisible(false)}
                extra={
                    <Space>
                        <Dropdown menu={{ items: Object.keys(SG_TEMPLATES).map((k) => ({ key: k, label: k })), onClick: ({ key }) => addTemplate(key) }}>
                            <Button icon={<ThunderboltOutlined />}>{t('Quick add')}</Button>
                        </Dropdown>
                        <Button type="primary" icon={<PlusOutlined />} onClick={showAddRuleModal}>
                            {t('Add rule')}
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={ruleColumns}
                    dataSource={rules}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    loading={rulesLoading}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={t('No rules yet. Click "Add rule" in the top right.')}
                            />
                        )
                    }}
                />
            </Drawer>

            {/* Add Rule Modal */}
            <Modal
                title={t('Add rule')}
                open={addRuleModalVisible}
                onCancel={() => setAddRuleModalVisible(false)}
                onOk={() => ruleForm.submit()}
            >
                <Form form={ruleForm} layout="vertical" onFinish={handleAddRule}>
                    <SgRuleFields form={ruleForm} />
                </Form>
            </Modal>
        </div>
    );
};

export default MySecurityGroup;
